import axios from "axios"; // Use axios directly for Vue 3
import {
  ajaxErrorHandler,
  createServerEndpoint,
  extractAJAXErrorValues,
  getRelativeDate
} from "@/utils/functions";
import { CONTROLS_SORT_BY } from "@/utils/constants";
import CONSTANTS from "@/static/constants";
import ROUTE_URL from "@/router/router-constants";
import ERRORS from "@/static/error-constants";
import { customLocalStorage } from "@/utils/custom-local-storage";
import { LookBackDate, LookBackOptions, SelectOption } from "@/rca-types";
import { ICaseCountry, ICaseStore } from "@/types/casesFrontendTypes";
import { RiskControlType } from "@/types/stats";
import { ControlsMutationEnum } from "./static/controls-mutation-enum";
import {
  buildUserScopedCacheKey,
  cloneCacheValue,
  ONE_HOUR_MS,
  pruneExpiredCacheEntries,
  setCacheEntryWithLimit
} from "@/store/cache-utils";

const storedFiltersParsed = customLocalStorage.getFilters();
const normalizedStoredLookBackTextValue =
  storedFiltersParsed?.lookBackTextValue === "All-time"
    ? "1 year"
    : storedFiltersParsed?.lookBackTextValue;

type ControlCasesCacheEntry = {
  fetchedAt: number;
  data: any;
  paging: any;
};

const CONTROL_CASES_CACHE_TTL_MS = ONE_HOUR_MS;
const CONTROL_CASES_CACHE_MAX_ENTRIES = 100;
const controlCasesCache = new Map<string, ControlCasesCacheEntry>();
const controlCasesRequests: Partial<Record<string, Promise<void>>> = {};
let controlCasesCacheGeneration = 0;

function buildControlCasesCacheKey(request: any, userId: string): string {
  return buildUserScopedCacheKey(request, userId, "request");
}

function clearControlCasesCache(): void {
  controlCasesCache.clear();
  controlCasesCacheGeneration += 1;
}

const state = () => ({
  search: "",
  riskLevel: [CONSTANTS.MEDIUM, CONSTANTS.HIGH] as string[] | null,
  riskLevelOldVal: null as string[] | null,
  statusOldVal: null as SelectOption[] | null,
  reasonCodes: storedFiltersParsed?.reasonCodes || null,
  purchaseTenders: storedFiltersParsed?.purchaseTenders || null,
  amountValue: storedFiltersParsed?.amountValue || null,
  amountOriginalValue: storedFiltersParsed?.amountOriginalValue || null,
  amountRefundValue: storedFiltersParsed?.amountRefundValue || null,
  amountPriceAdjValue: storedFiltersParsed?.amountPriceAdjValue || null,
  returnTenders: storedFiltersParsed?.returnTenders || null,
  controlCasesFetched: false,
  currentCaseFetched: false,
  controlTransactionsFetched: false,
  controlTransactions: null,
  controlCases: null,
  controlCaseOptions: null,
  embedParams: null,
  currentCase: null,
  original: null,
  currentValue: null,
  transactions: [] as any[],
  countries: storedFiltersParsed?.countries || null,
  stores: storedFiltersParsed?.stores || null,
  currentStores: null as string[] | null,
  status: storedFiltersParsed?.status || null,
  currentStatus: null as string[] | null,
  filterControlType: storedFiltersParsed?.filterControlType || null,
  lookBackTextValue: normalizedStoredLookBackTextValue ?? "1 week",
  lookBackDateValue: null as LookBackDate,
  today: new Date(),
  overdue: false,
  pageLoadingError: null,
  loading: false,
  paging: {
    page: 1,
    page_size: 15,
    total: 0,
    sort: CONTROLS_SORT_BY.RISK_LEVEL,
    sort_ascending: true
  },
  riskControlType: null as RiskControlType | null
});

const getters = {
  loading(state: any): boolean {
    return state.loading;
  },
  countryData(state: any): ICaseCountry[] {
    if (!state.controlCases) return [];
    return state.controlCaseOptions?.country || [];
  },
  storeData(state: any): ICaseStore[] {
    if (!state.controlCases) return [];
    return state.controlCaseOptions?.store || [];
  },
  statusData(state: any): string[] {
    if (!state.controlCases) return [];
    const status = state.controlCaseOptions?.status || [];
    if (!status.includes(CONSTANTS.CLOSED)) status.push(CONSTANTS.CLOSED);
    return status;
  },
  purchaseTenderData(state: any): string[] {
    if (!state.controlCases) return [];
    return state.controlCaseOptions?.purchaseTenders || [];
  },
  returnTenderData(state: any): string[] {
    if (!state.controlCases) return [];
    return state.controlCaseOptions?.returnTenders || [];
  },
  riskReasons(state: any): string[] {
    if (!state.controlCases) return [];
    return state.controlCaseOptions?.reasonCode || [];
  }
};

const mutations = {
  UPDATE_CONTROL_CASES(state: any, payload: any) {
    state.controlCasesFetched = true;
    state.controlCases = payload;
  },
  CONTROL_CASES_FETCH(state: any) {
    state.controlCasesFetched = false;
  },
  SAVE_CONTROL_DASHBOARD_OPTIONS(state: any, payload: any) {
    state.controlCaseOptions = payload;
  },
  REGISTER_PAGE_LOAD_ERROR(state: any, error: any) {
    state.pageLoadingError = error;
  },
  REGISTER_LOADING(state: any, isLoading: boolean) {
    state.loading = isLoading;
  },
  SAVE_CONTROL_PAGING_OPTIONS(state: any, payload: any) {
    state.paging = {
      page: payload.page,
      page_size: payload.page_size,
      total: payload.total,
      sort: payload.sort,
      sort_ascending: payload.sort_ascending
    };
  },
  UPDATE_RISK_CONTROL_TYPE(state: any, type: any) {
    state.riskControlType = type;
  },
  CURRENT_CONTROL_CASE_FETCH(state: any) {
    state.currentCaseFetched = false;
  },
  UPDATE_CURRENT_CONTROL_CASE(state: any, payload: any) {
    state.currentCaseFetched = true;
    state.currentCase = payload;
  },
  UPDATE_EMBED_PARAMS(state: any, payload: any) {
    state.embedParams = payload;
  },
  CONTROL_TRANSACTIONS_FETCH(state: any) {
    state.controlTransactionsFetched = false;
  },
  UPDATE_CONTROL_TRANSACTIONS(state: any, payload: any) {
    state.controlTransactionsFetched = true;
    state.controlTransactions = payload;
  },
  SEARCH(state: any, searchEntry: string) {
    state.search = searchEntry;
  },
  RISK_LEVEL(state: any, entry: string[]) {
    state.riskLevel = entry;
  },
  RISK_LEVEL_PREVIOUS_VALUE(state: any, entry: string[]) {
    state.riskLevelOldVal = entry;
  },
  STATUS_PREVIOUS_VALUE(state: any, entry: SelectOption[]) {
    state.statusOldVal = entry;
  },
  REASON_CODE_FILTER(state: any, entry: any[]) {
    state.reasonCodes = entry;
  },
  COUNTRY_FILTER(state: any, entry: ICaseCountry[]) {
    state.countries = entry;
  },
  STORES_FILTER(state: any, entry: ICaseStore[]) {
    state.stores = entry;
  },
  STATUS_FILTER(state: any, entry: SelectOption[]) {
    state.status = entry;
  },
  CONTROL_TYPE_FILTER(state: any, entry: SelectOption[]) {
    state.filterControlType = entry;
  },
  CURRENT_STORES(state: any, entry: string[]) {
    state.currentStores = entry;
  },
  PURCHASE_TENDER_FILTER(state: any, entry: string[]) {
    state.purchaseTenders = entry;
  },
  RETURN_TENDER_FILTER(state: any, entry: string[]) {
    state.returnTenders = entry;
  },
  AMOUNT_FILTER(state: any, entry: SelectOption) {
    state.amountValue = entry;
  },
  AMOUNT_ORIGINAL_FILTER(state: any, entry: SelectOption) {
    state.amountOriginalValue = entry;
  },
  AMOUNT_REFUND_FILTER(state: any, entry: SelectOption) {
    state.amountRefundValue = entry;
  },
  AMOUNT_PRICE_ADJ_VALUE_FILTER(state: any, entry: SelectOption) {
    state.amountPriceAdjValue = entry;
  },
  CASE_OVERDUE_FILTER(state: any, entry: boolean) {
    state.overdue = entry;
  },
  LOOK_BACK_FILTER(state: any, { textValue, dateValue, today }: any) {
    state.lookBackTextValue = textValue;
    state.lookBackDateValue = dateValue;
    state.today = today;
  },
  CONTROL_FILTERS(state: any, filters: any) {
    Object.assign(state, filters);
  },
  CLEAR_CONTROL_FILTERS(state: any) {
    state.stores = [];
    state.reasonCodes = [];
    state.status = [];
    state.lookBackTextValue = "";
    state.purchaseTenders = [];
    state.returnTenders = [];
    state.filterControlType = [];
    state.amountValue = "";
    state.amountOriginalValue = "";
    state.amountPriceAdjValue = "";
    state.amountRefundValue = "";
  },
  CURRENT_CONTROL_CASE_STATUS(state: any, payload: any) {
    state.original = payload;
    state.currentValue = payload ? payload.status : null;
  }
};

const actions = {
  async FETCH_CONTROL_CASES({ commit, state, rootState }: any, req: any) {
    if (state.loading) {
      console.log("Skipping API call - already loading");
      return;
    }
    
    const request = req.request;
    const shouldBypassCache = Boolean(req?.forceRefresh || req?.bypassCache);
    const userId = rootState?.currentUser?.id || "anonymous";
    const cacheKey = buildControlCasesCacheKey(request, userId);
    const requestCacheGeneration = controlCasesCacheGeneration;

    if (!shouldBypassCache) {
      pruneExpiredCacheEntries(controlCasesCache, CONTROL_CASES_CACHE_TTL_MS);
      const cachedEntry = controlCasesCache.get(cacheKey);
      const isFresh =
        cachedEntry &&
        Date.now() - cachedEntry.fetchedAt < CONTROL_CASES_CACHE_TTL_MS;

      if (isFresh) {
        commit("UPDATE_CONTROL_CASES", cloneCacheValue(cachedEntry.data));
        commit(
          "SAVE_CONTROL_PAGING_OPTIONS",
          cloneCacheValue(cachedEntry.paging)
        );
        return;
      }

      if (controlCasesRequests[cacheKey]) {
        await controlCasesRequests[cacheKey];
        return;
      }
    }

    const casesEndPoint = createServerEndpoint(ROUTE_URL.RISK_CONTROLS_CASES);
    const requestPromise = (async () => {
      try {
        customLocalStorage.setRequestParams(request);
        commit(ControlsMutationEnum.REGISTER_LOADING, true);
        commit("CONTROL_CASES_FETCH");
        const { data } = await axios.get<any>(casesEndPoint, {
          params: request
        });
        commit("UPDATE_CONTROL_CASES", data);
        
        // Save cases data to localStorage for navigation
        customLocalStorage.setCases({
          request: request,
          data: data
        });
        
        const paginationData = {
          page: Math.round((request.offset + 1) / request.limit) + 1,
          page_size: request.limit,
          total: data?.total || 0,
          sort: request.sort,
          sort_ascending: request.sort_ascending
        };
        commit("SAVE_CONTROL_PAGING_OPTIONS", paginationData);

        if (!shouldBypassCache) {
          const cacheWasInvalidatedDuringRequest =
            requestCacheGeneration !== controlCasesCacheGeneration;

          if (!cacheWasInvalidatedDuringRequest) {
            setCacheEntryWithLimit(
              controlCasesCache,
              cacheKey,
              {
                fetchedAt: Date.now(),
                data: cloneCacheValue(data),
                paging: cloneCacheValue(paginationData)
              },
              CONTROL_CASES_CACHE_MAX_ENTRIES
            );
          }
        }
      } catch (err: any) {
        const extracted = extractAJAXErrorValues(err);
        const formattedError = ajaxErrorHandler(extracted);
        commit("REGISTER_PAGE_LOAD_ERROR", formattedError);
        // Set controlCasesFetched to true even on error to prevent infinite loading
        commit("UPDATE_CONTROL_CASES", { case: [], total: 0 });
      } finally {
        commit(ControlsMutationEnum.REGISTER_LOADING, false);
      }
    })().finally(() => {
      if (!shouldBypassCache) {
        delete controlCasesRequests[cacheKey];
      }
    });

    if (!shouldBypassCache) {
      controlCasesRequests[cacheKey] = requestPromise;
    }

    await requestPromise;
  },
  async FETCH_SINGLE_CONTROL_CASE({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint(ROUTE_URL.RISK_CONTROLS_DETAILS);
    const endpoint = endpointRaw.replace("{case_id}", id);
    try {
      commit("CURRENT_CONTROL_CASE_FETCH");
      const { data } = await axios.get<any>(endpoint);
      commit("UPDATE_CURRENT_CONTROL_CASE", data);
    } catch (error: any) {
      const pageError = {
        code: error.response.status,
        endpoint: error.response.config.url,
        summary: ERRORS.LOAD_ERROR_SINGLE_CASE
      };
      commit("REGISTER_PAGE_LOAD_ERROR", pageError);
    }
  },
  async FETCH_CONTROL_TRANSACTIONS_DATA({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint(
      ROUTE_URL.RISK_CONTROLS_TRANSACTION
    );
    const endpoint = endpointRaw.replace("{case_id}", id);
    try {
      commit("CONTROL_TRANSACTIONS_FETCH");
      const { data } = await axios.get<any[]>(endpoint);
      commit("UPDATE_CONTROL_TRANSACTIONS", data);
    } catch (error: any) {
      const extracted = extractAJAXErrorValues(error);
      const formattedError = ajaxErrorHandler(extracted);
      commit("REGISTER_PAGE_LOAD_ERROR", formattedError);
    }
  },
  async UPDATE_CURRENT_CONTROL_CASE_STATUS({ dispatch }: any, input: any) {
    const { id, skipSingleCaseRefresh, ...payload } = input;

    const endpointRaw = createServerEndpoint(
      ROUTE_URL.RISK_CONTROLS_CASE_STATUS
    );

    // Interpolate incoming value: find & replace
    const endpoint = endpointRaw.replace("{case_id}", id);

    let result: any;
    try {
      const { data } = await axios.put<any>(endpoint, payload);
      // if (typeof gtag != "undefined") {
      //   gtag.event("Risk controls", {
      //     event_category: "Controls resolution",
      //     event_label: "status-update",
      //     value: input.status
      //   });
      // }
      result = data;
      clearControlCasesCache();
    } catch (error: any) {
      // Extracted 'AJAXErrorValues' from the error object
      // const extracted = extractAJAXErrorValues(error);

      // Only pass in primitive values to 'ajaxErrorHandler'
      // const formattedError = ajaxErrorHandler(extracted);
      // if(typeof ga != 'undefined') {
      //   ga("send", "exception", {
      //     exDescription: formattedError.summary,
      //     exFatal: false
      //   });
      // }
      console.error(error, result);
      // result = {
      //   additional_comments: null,
      //   adjusted_reason_code: null,
      //   case_id: id,
      //   modified_by: "",
      //   investigation_result: null,
      //   result_explanation: null,
      //   detailed_result_explanation: null,
      //   status: CONSTANTS.NEW_CASE
      // };
    }
    const isCaseListPage = [
      ROUTE_URL.ROUTE_PATH_TENDER_DEVIATION,
      ROUTE_URL.ROUTE_PATH_ARTICLE_NON_ARTICLE,
      ROUTE_URL.ROUTE_PATH_RR,
      ROUTE_URL.ROUTE_PATH_RPA,
      ROUTE_URL.ROUTE_PATH_SG80
    ].includes(location.pathname);

    if (!isCaseListPage && !skipSingleCaseRefresh) {
      // location.reload();
      await dispatch("FETCH_SINGLE_CONTROL_CASE", input.id);
    }

    return {};
  },
  async FETCH_EMBED_PARAMS({ commit }: any) {
    commit(ControlsMutationEnum.REGISTER_LOADING, true);

    let result: any;
    try {
      const { data } = await axios.post<any>(
        createServerEndpoint(ROUTE_URL.ACTION_TOKEN)
      );
      result = data;
      const error = data
        ? null
        : {
            code: 500,
            summary: ERRORS.LOAD_ERROR
          };
      commit(ControlsMutationEnum.REGISTER_PAGE_LOAD_ERROR, error);
    } catch (error: any) {
      // Extracted 'AJAXErrorValues' from the error object
      const extracted = extractAJAXErrorValues(error);

      // Only pass in primitive values to 'ajaxErrorHandler'
      const formattedError = ajaxErrorHandler(extracted);
      // if(typeof ga != 'undefined') {
      //   ga("send", "exception", {
      //     exDescription: formattedError.summary,
      //     exFatal: false
      //   });
      // }

      console.error("FETCH_EMBED_PARAMS", error);
      result = null;
      commit(ControlsMutationEnum.REGISTER_PAGE_LOAD_ERROR, formattedError);
    }
    commit(ControlsMutationEnum.UPDATE_EMBED_PARAMS, result);
    commit(ControlsMutationEnum.REGISTER_LOADING, false);
    return result;
  },
  async SEARCH_FILTER({ commit }: any, searchEntry: string) {
    commit("SEARCH", searchEntry);
  },
  async RISK_LEVEL_FILTER({ commit }: any, entry: string[]) {
    commit("RISK_LEVEL", entry);
  },
  async RISK_LEVEL_PREVIOUS_VALUE({ commit }: any, entry: string[]) {
    commit("RISK_LEVEL_PREVIOUS_VALUE", entry);
  },
  async STATUS_PREVIOUS_VALUE({ commit }: any, entry: SelectOption[]) {
    commit("STATUS_PREVIOUS_VALUE", entry);
  },
  async REASON_CODE_FILTER({ commit }: any, entry: any[]) {
    commit("REASON_CODE_FILTER", entry);
  },
  async COUNTRY_FILTER({ commit }: any, entry: ICaseCountry[]) {
    commit("COUNTRY_FILTER", entry);
  },
  async STORES_FILTER({ commit }: any, entry: ICaseStore[]) {
    commit("STORES_FILTER", entry);
  },
  async STATUS_FILTER({ commit }: any, entry: SelectOption[]) {
    commit("STATUS_FILTER", entry);
  },
  async CONTROL_TYPE_FILTER({ commit }: any, entry: SelectOption[]) {
    commit("CONTROL_TYPE_FILTER", entry);
  },
  async CURRENT_STORES({ commit }: any, entry: string[]) {
    commit("CURRENT_STORES", entry);
  },
  async PURCHASE_TENDER_FILTER({ commit }: any, entry: string[]) {
    commit("PURCHASE_TENDER_FILTER", entry);
  },
  async RETURN_TENDER_FILTER({ commit }: any, entry: string[]) {
    commit("RETURN_TENDER_FILTER", entry);
  },
  async AMOUNT_FILTER({ commit }: any, entry: SelectOption) {
    commit("AMOUNT_FILTER", entry);
  },
  async AMOUNT_ORIGINAL_FILTER({ commit }: any, entry: SelectOption) {
    commit("AMOUNT_ORIGINAL_FILTER", entry);
  },
  async AMOUNT_REFUND_FILTER({ commit }: any, entry: SelectOption) {
    commit("AMOUNT_REFUND_FILTER", entry);
  },
  async AMOUNT_PRICE_ADJ_VALUE_FILTER({ commit }: any, entry: SelectOption) {
    commit("AMOUNT_PRICE_ADJ_VALUE_FILTER", entry);
  },
  async CASE_OVERDUE_FILTER({ commit }: any, entry: boolean) {
    commit("CASE_OVERDUE_FILTER", entry);
  },
  async LOOK_BACK_FILTER({ commit }: any, textValue: LookBackOptions) {
    const latestDate = new Date();
    const dateValue = getRelativeDate(textValue, latestDate);
    commit("LOOK_BACK_FILTER", { textValue, dateValue, today: latestDate });
  },
  async CONTROL_FILTERS({ commit }: any, filters: any) {
    commit("CONTROL_FILTERS", filters);
  },
  CLEAR_CONTROL_FILTERS({ commit }: any) {
    commit("CLEAR_CONTROL_FILTERS");
  },
  async FETCH_CURRENT_CONTROL_CASE_STATUS({ commit }: any, id: string) {
    const endpointRaw = createServerEndpoint(
      ROUTE_URL.RISK_CONTROLS_CASE_STATUS
    );

    // Interpolate incoming value: find & replace
    const endpoint = endpointRaw.replace("{case_id}", id);

    let result: any;
    try {
      const { data } = await axios.get<any>(endpoint);
      result = data;
    } catch (error: any) {
      // Extracted 'AJAXErrorValues' from the error object
      // const extracted = extractAJAXErrorValues(error);

      // Only pass in primitive values to 'ajaxErrorHandler'
      // const formattedError = ajaxErrorHandler(extracted);

      console.error(error);
      result = null;
    }

    commit("CURRENT_CONTROL_CASE_STATUS", result);
  },
  async RISK_CONTROL_TYPE({ commit }: any, type: RiskControlType) {
    commit("UPDATE_RISK_CONTROL_TYPE", type);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
